import { getAccessToken } from '@/lib/tokenStorage';

import { enhancedApi } from '../api/baseApi';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export type FreshStrawAccountOption = {
  accountId: string;
  accountCode: string;
  accountName: string;
};

export type FreshStrawPredictionTech = {
  id: string;
  name: string;
};

export type FreshStrawOutsideEmbryo = {
  id: string;
  name: string;
  implantDate?: string | null;
  transferSiteId?: string | null;
  transferSiteName: string;
  transferOwnerId?: string | null;
  transferOwnerName: string;
  donorName: string;
  donorReg: string;
  sireName: string;
  freezeQuantity?: number | null;
  transferQuantity?: number | null;
  stateCode?: number | null;
};

export type FreshStrawOutsideEmbryoUpdateRequest = {
  outsideEntryId: string;
  stateCode?: number;
  transferDate?: string;
  transferSiteId?: string;
  transferOwnerId?: string;
};

export type FreshStrawStageGradeEntry = {
  stage: number;
  grade: number;
  count: number;
};

type FreshStrawSaveBase = {
  fertId: string;
  siteId: string;
  grader?: string | null;
  graderId?: string | null;
  loadById?: string | null;
  ehmDate?: string | null;
  strawLotNo?: string | null;
  strawIncubatorNo?: string | null;
  gunWarmerTemp?: string | null;
  loadDate?: string | null;
  embryoStagingId?: string | null;
  entries: FreshStrawStageGradeEntry[];
};

export type FreshStrawImplantRequest = FreshStrawSaveBase & {
  transferDate: string;
  farmLocationId?: string | null;
  autoAssignStraws: boolean;
  freshStrawNum?: string | null;
  calfType?: number | null;
  recip: string;
};

export type FreshStrawFreezeRequest = FreshStrawSaveBase & {
  freezeDate: string;
  freezerId?: string | null;
  freshStrawNum?: string | null;
};

export type FreshStrawDiscardRequest = FreshStrawSaveBase & {
  freshStrawNum?: string | null;
};

export type FreshStrawSaveResponse = {
  success: boolean;
  message?: string;
  billableShipped?: string;
};

const buildQueryString = (params: Record<string, string>) => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      query.set(key, value);
    }
  }
  return query.toString();
};

export type FreshStrawPartnershipShare = {
  accountId?: string | null;
  accountName?: string | null;
  accountCode?: string | null;
  percent?: number | null;
};

export type FreshStrawFertilization = {
  fertilizationId: string;
  donorScheduleId?: string | null;
  donorId?: string | null;
  donorName?: string | null;
  mating?: string | null;
  ivcNumber?: number | null;
  cleaved?: number | null;
  gradeOneDiscarded?: number | null;
  gradeTwoDiscarded?: number | null;
  gradeThreeDiscarded?: number | null;
  transferred?: number | null;
  heldTransfer?: number | null;
  degenerates?: number | null;
  clientShipped?: number | null;
  frozen?: number | null;
  shippedTog?: number | null;
  origShipped?: number | null;
  heldFrozen?: number | null;
  clientTransfer?: number | null;
  clientFrozen?: number | null;
  clientDiscarded?: number | null;
  billable?: number | null;
  billableVerified?: boolean | null;
  inStraws?: number | null;
  priority?: number | null;
  ivfComment?: string | null;
  plateNumber?: number | null;
  backgroundColor?: string | null;
  partnership?: FreshStrawPartnershipShare[] | null;
  sireRegistrationNumber?: string | null;
  sireBreedName?: string | null;
  donorRegistrationNumber?: string | null;
  donorBreedName?: string | null;
  naab?: string | null;
};

export type FreshStrawDegenerateUpdate = {
  fertilizationId: string;
  degenerates: number;
};

export type FreshStrawEmbryoPatch = {
  mating?: string | null;
  clientRecipient?: string | null;
  implantDate?: string | null;
  siteId?: string | null;
  strawNumber?: number | null;
  freshStrawNo?: number | null;
  embTagNumber?: number | null;
  cane?: string | null;
  rightLeft?: number | null;
  quality?: number | null;
  distance?: number | null;
  comment?: string | null;
  stage?: number | null;
  grade?: number | null;
  estrusSyncNumber?: number | null;
  gnrhUsedAtImplant?: boolean | null;
};

export type FreshStrawFertPatch = {
  ivcNumber?: number | null;
  cleaved?: number | null;
  heldTransfer?: number | null;
  discardedGrade1?: number | null;
  discardedGrade2?: number | null;
  discardedGrade3?: number | null;
  degenerates?: number | null;
  heldFrz?: number | null;
  transferClient?: number | null;
  frozeClient?: number | null;
  discardClient?: number | null;
  loadedInStraws?: number | null;
};

export type FreshStrawEmbryoPartnership = {
  partnershipId: string;
  embryoId: string;
  accountId?: string | null;
  accountCode?: string | null;
  accountName?: string | null;
  percent?: number | null;
  beginDate?: string | null;
  endDate?: string | null;
  name?: string | null;
};

export type FreshStrawTripMeta = {
  stopId: number;
  kickOuts?: string | null;
  comments?: string | null;
  mileage?: number | null;
  priceTier?: string | null;
  shipmentId?: string | null;
  tripTankId?: string | null;
  tripTankName?: string | null;
  tripComment?: string | null;
};

export type FreshStrawTripMetaUpdate = {
  kickOuts?: string | null;
  comments?: string | null;
  mileage?: number | null;
  priceTier?: string | null;
  tripTankId?: string | null;
  tripComment?: string | null;
};

export type FreshStrawImplantDetail = {
  embryoId: string;
  mating?: string | null;
  strawNumber?: string | null;
  stage?: number | null;
  grade?: number | null;
  status?: number | null;
  dairyCompId?: string | null;
  recip?: string | null;
  transferTechId?: string | null;
  transferTechName?: string | null;
  clLeftRight?: string | null;
  quality?: string | null;
  distance?: string | null;
  earTag?: string | null;
  farmLocationName?: string | null;
  transferComment?: string | null;
};

export type FreshStrawTransferredEmbryo = {
  embryoId?: string | null;
  flushId?: string | null;
  fertilizationId?: string | null;
  aspDate?: string | null;
  aspSite?: string | null;
  ranchCode?: string | null;
  partnerCount?: number | null;
  sex?: string | null;
  mating?: string | null;
  quality?: number | null;
  distance?: number | null;
  stage?: number | null;
  grade?: number | null;
  strawNumber?: number | null;
  tagNumber?: number | null;
  cane?: string | null;
  recipName?: string | null;
  implantComment?: string | null;
  estrusSync?: string | null;
  clValue?: string | null;
};

export type FreshStrawFlushOption = {
  flushId: string;
  name?: string | null;
};

export type FreshStrawFlushEmbryoRow = {
  embryoId: string;
  embTagNumber?: number | null;
  strawNumber?: number | null;
  freshStrawNumber?: number | null;
  stage?: number | null;
  grade?: number | null;
  status?: number | null;
  freshFrozen?: number | null;
  comm?: string | null;
};

export type FreshStrawFlushDetail = {
  flushId: string;
  name?: string | null;
  flushDate?: string | null;
  flushTime?: string | null;
  status?: number | null;
  donorId?: string | null;
  donorName?: string | null;
  sireId?: string | null;
  sireName?: string | null;
  ownerId?: string | null;
  ownerCode?: string | null;
  totalCollected?: number | null;
  transferred?: number | null;
  frozen?: number | null;
  unFertilized?: number | null;
  degenerates?: number | null;
  grade3?: number | null;
  leftCl?: number | null;
  rightCl?: number | null;
  comment?: string | null;
  caneNumber?: string | null;
  semenType?: number | null;
  freshEmbryos: FreshStrawFlushEmbryoRow[];
  frozenEmbryos: FreshStrawFlushEmbryoRow[];
};

export type FreshStrawEnsureFlushEmbryosRequest = {
  graderEquipmentId?: string | null;
  stopId?: number | null;
};

export type FreshStrawFrozenEmbryo = {
  embryoId: string;
  fertilizationId?: string | null;
  mating?: string | null;
  cane?: string | null;
  strawNumber?: string | null;
  stage?: number | null;
  grade?: number | null;
  status?: string | null;
  dairyCompId?: string | null;
  ranchCode?: string | null;
  tagNumber?: string | null;
  recipName?: string | null;
  estrusSyncNumber?: string | null;
  implantComment?: string | null;
  quality?: number | null;
  distance?: number | null;
  gnrhUsedAtImplant?: boolean | null;
  clLeftRight?: string | null;
  partnerCount?: number | null;
};

export type FreshStrawVerifyBillableResponse = {
  success: boolean;
  results: string[];
};

export type FreshStrawTransferImportResponse = {
  parsed: number;
  saved: number;
  errors: string[];
};

export type FreshStrawTransferImportArgs = {
  file: File;
  siteId?: string;
  farmLocationId?: string;
  transferDate?: string;
  dairyComp?: boolean;
};

export type FreshStrawPregCheckImportResponse = {
  parsed: number;
  saved: number;
  errors: string[];
};

export type FreshStrawPregCheckImportArgs = {
  file: File;
  defaultSiteName?: string;
  faria?: boolean;
};

export type FreshStrawFreshEmbryoImportResponse = {
  parsed: number;
  saved: number;
  skipped: number;
  updated: number;
  errors: string[];
};

export type FreshStrawFreshEmbryoImportArgs = {
  file: File;
};

export type FreshStrawSiteLookupResponse = {
  id: string;
  name: string | null;
};

export type FreshStrawAccountLookupResponse = {
  id: string;
  code: string | null;
  name: string | null;
};

async function uploadFreshStraw<T>(
  path: string,
  formData: FormData,
): Promise<{ data?: T; error?: { status: number | string; data: string } }> {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'POST',
      headers: {
        // NOTE: don't set Content-Type — the browser fills in the
        // multipart boundary when you pass a FormData body.
        Authorization: `Bearer ${token}`,
        'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
      },
      credentials: 'include',
      body: formData,
      signal: AbortSignal.timeout(300_000),
    });

    if (!response.ok) {
      const error = await response.text();
      return { error: { status: response.status, data: error } };
    }
    const data = (await response.json()) as T;
    return { data };
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === 'TimeoutError') {
      return {
        error: {
          status: 'TIMEOUT',
          data: 'Upload timed out after 5 minutes. The file may be too large — try splitting it into smaller batches.',
        },
      };
    }
    return {
      error: {
        status: 'FETCH_ERROR',
        data: error instanceof Error ? error.message : 'Unknown error',
      },
    };
  }
}

async function fetchFreshStraw<T>(
  path: string,
  method: 'GET' | 'PUT' | 'POST' | 'PATCH' | 'DELETE' = 'GET',
  body?: unknown,
): Promise<{ data?: T; error?: { status: number | string; data: string } }> {
  try {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      const error = await response.text();
      return { error: { status: response.status, data: error } };
    }

    const data = (await response.json()) as T;
    return { data };
  } catch (error: unknown) {
    return {
      error: {
        status: 'FETCH_ERROR',
        data: error instanceof Error ? error.message : 'Unknown error',
      },
    };
  }
}

export const freshStrawRestApi = enhancedApi.injectEndpoints({
  endpoints: (build) => ({
    getFreshStrawAccountsRest: build.query<
      FreshStrawAccountOption[],
      {
        fertilizationDate: string;
        transferDate: string;
        fertilizationSite: string;
        transferSite: string;
      }
    >({
      queryFn: async (queryArg) => {
        const query = buildQueryString({
          fertilizationDate: queryArg.fertilizationDate,
          transferDate: queryArg.transferDate,
          fertilizationSite: queryArg.fertilizationSite,
          transferSite: queryArg.transferSite,
        });
        return fetchFreshStraw<FreshStrawAccountOption[]>(
          `/api/fresh-straw/accounts?${query}`,
        );
      },
      providesTags: ['Lookups'],
    }),
    getFreshStrawPredictionTechsRest: build.query<
      FreshStrawPredictionTech[],
      void
    >({
      queryFn: async () => {
        return fetchFreshStraw<FreshStrawPredictionTech[]>(
          '/api/fresh-straw/prediction-techs',
        );
      },
      providesTags: ['PredictionTechs'],
    }),
    getOfTransferOutsideEmbryosRest: build.query<
      FreshStrawOutsideEmbryo[],
      {
        transferDate: string;
        transferSite: string;
        accountId?: string;
      }
    >({
      queryFn: async (queryArg) => {
        const query = buildQueryString({
          transferDate: queryArg.transferDate,
          transferSite: queryArg.transferSite,
          accountId: queryArg.accountId ?? '',
        });
        return fetchFreshStraw<FreshStrawOutsideEmbryo[]>(
          `/api/fresh-straw/of-outside-embryos?${query}`,
        );
      },
      providesTags: ['Lookups'],
    }),
    updateOutsideEmbryosRest: build.mutation<
      { success: boolean; message?: string }[],
      FreshStrawOutsideEmbryoUpdateRequest[]
    >({
      queryFn: async (requestBody) =>
        fetchFreshStraw<{ success: boolean; message?: string }[]>(
          '/api/embryo/outside',
          'PUT',
          requestBody,
        ),
      invalidatesTags: ['Lookups'],
    }),
    postFreshImplant: build.mutation<
      FreshStrawSaveResponse,
      FreshStrawImplantRequest
    >({
      queryFn: async (body) =>
        fetchFreshStraw<FreshStrawSaveResponse>(
          '/api/fresh-straw/implant',
          'POST',
          body,
        ),
      invalidatesTags: ['Lookups', 'FertilizationListFreshShipment'],
    }),
    postFreshFreeze: build.mutation<
      FreshStrawSaveResponse,
      FreshStrawFreezeRequest
    >({
      queryFn: async (body) =>
        fetchFreshStraw<FreshStrawSaveResponse>(
          '/api/fresh-straw/freeze',
          'POST',
          body,
        ),
      invalidatesTags: ['Lookups', 'FertilizationListFreshShipment'],
    }),
    postFreshDiscard: build.mutation<
      FreshStrawSaveResponse,
      FreshStrawDiscardRequest
    >({
      queryFn: async (body) =>
        fetchFreshStraw<FreshStrawSaveResponse>(
          '/api/fresh-straw/discard',
          'POST',
          body,
        ),
      invalidatesTags: ['Lookups', 'FertilizationListFreshShipment'],
    }),
    createFreshStrawDonor: build.mutation<
      { success: boolean; id: string },
      {
        name: string;
        breedId?: string | null;
        ownerId?: string | null;
        received?: string | null;
        sire?: string | null;
      }
    >({
      queryFn: async (body) =>
        fetchFreshStraw<{ success: boolean; id: string }>(
          '/api/fresh-straw/donors/new',
          'POST',
          body,
        ),
    }),
    createFreshStrawSire: build.mutation<
      { success: boolean; id: string },
      { sire: string; breedId?: string | null; ownerId?: string | null }
    >({
      queryFn: async (body) =>
        fetchFreshStraw<{ success: boolean; id: string }>(
          '/api/fresh-straw/sires/new',
          'POST',
          body,
        ),
    }),
    deactivateFreshStrawEmbryo: build.mutation<
      { success: boolean },
      { embryoId: string }
    >({
      queryFn: async ({ embryoId }) =>
        fetchFreshStraw<{ success: boolean }>(
          `/api/fresh-straw/embryos/${embryoId}/deactivate`,
          'POST',
          {},
        ),
      invalidatesTags: [
        'FreshStrawFrozenEmbryos',
        'FertilizationListFreshShipment',
      ],
    }),
    stageFreshStrawEmbryos: build.mutation<
      { staged: number; skipped: boolean; skippedReason?: string },
      {
        fertId: string;
        fertName?: string;
        fertDate: string;
        fertSiteId: string;
        transferDate: string;
        transferSiteId: string;
        farmLocationId?: string;
        accountId?: string;
        freshShipmentId?: string;
        calfType: number;
        loadDate: number;
        freshShippedNum: number;
      }
    >({
      queryFn: async (body) =>
        fetchFreshStraw<{
          staged: number;
          skipped: boolean;
          skippedReason?: string;
        }>(`/api/fresh-straw/embryo-staging/seed`, 'POST', body),
      invalidatesTags: ['FertilizationListFreshShipment'],
    }),
    implantFrozenFreshStrawEmbryo: build.mutation<
      { success: boolean },
      {
        embryoId: string;
        transferDate: string;
        transferSiteId: string;
        newStrawNumber: number;
        calfType: number;
        farmLocationId?: string;
        ehmDate: string;
        strawLotNo?: string;
        strawIncubatorNo?: string;
        thawThermosTemp?: string;
      }
    >({
      queryFn: async ({ embryoId, ...body }) =>
        fetchFreshStraw<{ success: boolean }>(
          `/api/fresh-straw/embryos/${embryoId}/implant-frozen`,
          'POST',
          body,
        ),
      invalidatesTags: [
        'FreshStrawFrozenEmbryos',
        'FertilizationListFreshShipment',
      ],
    }),
    markFreshStrawNoEmbryosProduced: build.mutation<
      { success: boolean },
      { fertilizationId: string }
    >({
      queryFn: async ({ fertilizationId }) =>
        fetchFreshStraw<{ success: boolean }>(
          `/api/fresh-straw/fertilizations/${fertilizationId}/no-embryos-produced`,
          'POST',
          {},
        ),
      invalidatesTags: ['FertilizationListFreshShipment'],
    }),
    reassignFreshStrawStraws: build.mutation<
      { success: boolean },
      { fertilizationId: string }
    >({
      queryFn: async ({ fertilizationId }) =>
        fetchFreshStraw<{ success: boolean }>(
          `/api/fresh-straw/fertilizations/${fertilizationId}/reassign-straws`,
          'POST',
          {},
        ),
      invalidatesTags: [
        'FreshStrawFrozenEmbryos',
        'FertilizationListFreshShipment',
      ],
    }),
    removeFreshStrawBsFlag: build.mutation<
      { success: boolean },
      { fertilizationId: string }
    >({
      queryFn: async ({ fertilizationId }) =>
        fetchFreshStraw<{ success: boolean }>(
          `/api/fresh-straw/fertilizations/${fertilizationId}/remove-bs-flag`,
          'POST',
          {},
        ),
      invalidatesTags: ['FertilizationListFreshShipment'],
    }),
    patchFreshStrawEmbryo: build.mutation<
      { success: boolean },
      { embryoId: string } & FreshStrawEmbryoPatch
    >({
      queryFn: async ({ embryoId, ...body }) =>
        fetchFreshStraw<{ success: boolean }>(
          `/api/fresh-straw/embryos/${embryoId}`,
          'PATCH',
          body,
        ),
      invalidatesTags: [
        'FreshStrawFrozenEmbryos',
        'FreshStrawFlushes',
        'FertilizationListFreshShipment',
      ],
    }),
    getFreshStrawEmbryoPartnerships: build.query<
      FreshStrawEmbryoPartnership[],
      { embryoId: string }
    >({
      queryFn: async (args) => {
        if (!args.embryoId) return { data: [] };
        return fetchFreshStraw<FreshStrawEmbryoPartnership[]>(
          `/api/fresh-straw/embryos/${args.embryoId}/partnerships`,
        );
      },
      providesTags: (_result, _err, arg) => [
        { type: 'FreshStrawEmbryoPartnerships', id: arg.embryoId },
      ],
    }),
    createFreshStrawEmbryoPartnership: build.mutation<
      FreshStrawEmbryoPartnership,
      { embryoId: string; accountId: string; percent: number }
    >({
      queryFn: async ({ embryoId, ...body }) =>
        fetchFreshStraw<FreshStrawEmbryoPartnership>(
          `/api/fresh-straw/embryos/${embryoId}/partnerships`,
          'POST',
          body,
        ),
      invalidatesTags: (_result, _err, arg) => [
        { type: 'FreshStrawEmbryoPartnerships', id: arg.embryoId },
      ],
    }),
    deleteFreshStrawEmbryoPartnership: build.mutation<
      { success: boolean },
      { embryoId: string; partnershipId: string }
    >({
      queryFn: async ({ embryoId, partnershipId }) =>
        fetchFreshStraw<{ success: boolean }>(
          `/api/fresh-straw/embryos/${embryoId}/partnerships/${partnershipId}`,
          'DELETE',
        ),
      invalidatesTags: (_result, _err, arg) => [
        { type: 'FreshStrawEmbryoPartnerships', id: arg.embryoId },
      ],
    }),
    endAllFreshStrawEmbryoPartnerships: build.mutation<
      { success: boolean },
      { embryoId: string }
    >({
      queryFn: async ({ embryoId }) =>
        fetchFreshStraw<{ success: boolean }>(
          `/api/fresh-straw/embryos/${embryoId}/partnerships/end-all`,
          'POST',
          {},
        ),
      invalidatesTags: (_result, _err, arg) => [
        { type: 'FreshStrawEmbryoPartnerships', id: arg.embryoId },
      ],
    }),
    getFreshStrawTripMeta: build.query<
      FreshStrawTripMeta | null,
      { stopId: number }
    >({
      queryFn: async (args) => {
        if (!args.stopId || args.stopId < 1) return { data: null };
        return fetchFreshStraw<FreshStrawTripMeta>(
          `/api/fresh-straw/trip-meta/${args.stopId}`,
        );
      },
      providesTags: ['FreshStrawTripMeta'],
    }),
    updateFreshStrawTripMeta: build.mutation<
      { success: boolean },
      { stopId: number } & FreshStrawTripMetaUpdate
    >({
      queryFn: async ({ stopId, ...body }) =>
        fetchFreshStraw<{ success: boolean }>(
          `/api/fresh-straw/trip-meta/${stopId}`,
          'PUT',
          body,
        ),
      invalidatesTags: ['FreshStrawTripMeta'],
    }),
    getFreshStrawOutsideEmbryosByStop: build.query<
      FreshStrawFrozenEmbryo[],
      { accountId: string; siteId: string; stopNumber: number }
    >({
      queryFn: async (args) => {
        if (!args.accountId || !args.siteId) return { data: [] };
        const query = buildQueryString({
          accountId: args.accountId,
          siteId: args.siteId,
          stopNumber: String(args.stopNumber),
        });
        return fetchFreshStraw<FreshStrawFrozenEmbryo[]>(
          `/api/fresh-straw/outside-embryos/by-stop?${query}`,
        );
      },
      providesTags: ['FreshStrawFrozenEmbryos'],
    }),
    getFreshStrawImplantDetails: build.query<
      FreshStrawImplantDetail[],
      {
        transferDate: string;
        transferSiteId: string;
        accountId?: string;
        freshShipmentId?: string;
        farmLocationName?: string;
      }
    >({
      queryFn: async (args) => {
        if (!args.transferSiteId) return { data: [] };
        const query = buildQueryString({
          transferDate: args.transferDate,
          transferSiteId: args.transferSiteId,
          accountId: args.accountId ?? '',
          freshShipmentId: args.freshShipmentId ?? '',
          farmLocationName: args.farmLocationName ?? '',
        });
        return fetchFreshStraw<FreshStrawImplantDetail[]>(
          `/api/fresh-straw/implant-details?${query}`,
        );
      },
      providesTags: ['FreshStrawFrozenEmbryos'],
    }),
    getFreshStrawTransferredEmbryos: build.query<
      FreshStrawTransferredEmbryo[],
      {
        accountId: string;
        siteId: string;
        stopNumber: number;
        implantDate: string;
      }
    >({
      queryFn: async (args) => {
        if (!args.accountId || !args.siteId) return { data: [] };
        const query = buildQueryString({
          accountId: args.accountId,
          siteId: args.siteId,
          stopNumber: String(args.stopNumber),
          implantDate: args.implantDate,
        });
        return fetchFreshStraw<FreshStrawTransferredEmbryo[]>(
          `/api/fresh-straw/transferred-embryos?${query}`,
        );
      },
      providesTags: ['FreshStrawFrozenEmbryos'],
    }),
    searchFreshStrawFertEmbryos: build.query<
      FreshStrawFrozenEmbryo[],
      { procId: string }
    >({
      queryFn: async (args) => {
        if (!args.procId) return { data: [] };
        const query = buildQueryString({ procId: args.procId });
        return fetchFreshStraw<FreshStrawFrozenEmbryo[]>(
          `/api/fresh-straw/fert-embryos/search?${query}`,
        );
      },
      providesTags: ['FreshStrawFrozenEmbryos'],
    }),
    searchFreshStrawOnFarmEmbryos: build.query<
      FreshStrawFrozenEmbryo[],
      { caneSearch: string; accountId?: string }
    >({
      queryFn: async (args) => {
        if (!args.caneSearch) return { data: [] };
        const query = buildQueryString({
          caneSearch: args.caneSearch,
          accountId: args.accountId ?? '',
        });
        return fetchFreshStraw<FreshStrawFrozenEmbryo[]>(
          `/api/fresh-straw/on-farm-embryos/search?${query}`,
        );
      },
      providesTags: ['FreshStrawFrozenEmbryos'],
    }),
    getFreshStrawFlushes: build.query<
      FreshStrawFlushOption[],
      { flushDate: string; siteId: string }
    >({
      queryFn: async (args) => {
        if (!args.siteId || !args.flushDate) return { data: [] };
        const query = buildQueryString({
          flushDate: args.flushDate,
          siteId: args.siteId,
        });
        return fetchFreshStraw<FreshStrawFlushOption[]>(
          `/api/fresh-straw/flushes?${query}`,
        );
      },
      providesTags: ['FreshStrawFlushes'],
    }),
    getFreshStrawFlushDetail: build.query<
      FreshStrawFlushDetail | null,
      { flushId: string }
    >({
      queryFn: async (args) => {
        if (!args.flushId) return { data: null };
        return fetchFreshStraw<FreshStrawFlushDetail>(
          `/api/fresh-straw/flushes/${args.flushId}`,
        );
      },
      providesTags: ['FreshStrawFlushes'],
    }),
    ensureFreshStrawFlushEmbryos: build.mutation<
      { success: boolean },
      { flushId: string } & FreshStrawEnsureFlushEmbryosRequest
    >({
      queryFn: async ({ flushId, ...body }) =>
        fetchFreshStraw<{ success: boolean }>(
          `/api/fresh-straw/flushes/${flushId}/ensure-embryos`,
          'POST',
          body,
        ),
      invalidatesTags: ['FreshStrawFlushes'],
    }),
    markFreshStrawNoStimNoFlush: build.mutation<
      { success: boolean },
      { flushId: string; comment?: string }
    >({
      queryFn: async ({ flushId, comment }) =>
        fetchFreshStraw<{ success: boolean }>(
          `/api/fresh-straw/flushes/${flushId}/no-stim-no-flush`,
          'POST',
          { comment },
        ),
      invalidatesTags: ['FreshStrawFlushes'],
    }),
    createFreshStrawOutsideEmbryo: build.mutation<
      { success: boolean; id: string },
      {
        accountId: string;
        donorId: string;
        sireId: string;
        siteId: string;
        stopNumber: number;
        transferDate: string;
        embryoType?: number;
        embryoSex?: number;
        stage?: number;
        grade?: number;
        tag?: string;
        straw?: string;
        cane?: string;
        comment?: string;
      }
    >({
      queryFn: async (body) =>
        fetchFreshStraw<{ success: boolean; id: string }>(
          '/api/fresh-straw/outside-embryos',
          'POST',
          body,
        ),
      invalidatesTags: ['Lookups'],
    }),
    getFreshStrawFrozenEmbryos: build.query<
      FreshStrawFrozenEmbryo[],
      {
        transferDate: string;
        transferSiteId: string;
        accountId?: string;
        freshShipmentId?: string;
        storageLocationId?: string;
        caneNumber?: string;
      }
    >({
      queryFn: async (args) => {
        if (!args.transferSiteId) return { data: [] };
        if (!args.storageLocationId && !args.caneNumber) return { data: [] };
        const query = buildQueryString({
          transferDate: args.transferDate,
          transferSiteId: args.transferSiteId,
          accountId: args.accountId ?? '',
          freshShipmentId: args.freshShipmentId ?? '',
          storageLocationId: args.storageLocationId ?? '',
          caneNumber: args.caneNumber ?? '',
        });
        return fetchFreshStraw<FreshStrawFrozenEmbryo[]>(
          `/api/fresh-straw/frozen-embryos?${query}`,
        );
      },
      providesTags: ['FreshStrawFrozenEmbryos'],
    }),
    getFreshStrawFrozenEmbryosByShipment: build.query<
      FreshStrawFrozenEmbryo[],
      { shipmentId: string }
    >({
      queryFn: async (args) => {
        if (!args.shipmentId) return { data: [] };
        const query = new URLSearchParams({
          shipmentId: args.shipmentId,
        }).toString();
        return fetchFreshStraw<FreshStrawFrozenEmbryo[]>(
          `/api/fresh-straw/frozen-embryos/by-shipment?${query}`,
        );
      },
      providesTags: ['FreshStrawFrozenEmbryos'],
    }),
    getFreshStrawFertilizations: build.query<
      FreshStrawFertilization[],
      {
        fertilizationDate: string;
        fertilizationSite?: string;
        accountId?: string;
        freshShipmentId?: string;
      }
    >({
      queryFn: async (args) => {
        const query = buildQueryString({
          fertilizationDate: args.fertilizationDate,
          fertilizationSite: args.fertilizationSite ?? '',
          accountId: args.accountId ?? '',
          freshShipmentId: args.freshShipmentId ?? '',
        });
        return fetchFreshStraw<FreshStrawFertilization[]>(
          `/api/fresh-straw/fertilizations?${query}`,
        );
      },
      providesTags: ['FertilizationListFreshShipment'],
    }),
    updateFreshStrawDegenerates: build.mutation<
      { success: boolean; rowsUpdated: number },
      FreshStrawDegenerateUpdate[]
    >({
      queryFn: async (body) =>
        fetchFreshStraw<{ success: boolean; rowsUpdated: number }>(
          '/api/fresh-straw/fertilizations/update-degenerates',
          'PUT',
          body,
        ),
      invalidatesTags: ['FertilizationListFreshShipment'],
    }),
    verifyFreshStrawBillable: build.mutation<
      FreshStrawVerifyBillableResponse,
      string[]
    >({
      queryFn: async (body) =>
        fetchFreshStraw<FreshStrawVerifyBillableResponse>(
          '/api/fresh-straw/fertilizations/verify-billable',
          'POST',
          body,
        ),
      invalidatesTags: ['FertilizationListFreshShipment'],
    }),
    verifyFreshShipment: build.mutation<
      { success: boolean },
      { freshShipmentId: string; verified: boolean }
    >({
      queryFn: async ({ freshShipmentId, verified }) =>
        fetchFreshStraw<{ success: boolean }>(
          `/api/fresh-straw/fresh-shipments/${freshShipmentId}/verify`,
          'POST',
          { verified },
        ),
      invalidatesTags: ['FertilizationListFreshShipment', 'FreshShipmentList'],
    }),
    importFreshStrawTransfers: build.mutation<
      FreshStrawTransferImportResponse,
      FreshStrawTransferImportArgs
    >({
      queryFn: async ({
        file,
        siteId,
        farmLocationId,
        transferDate,
        dairyComp,
      }) => {
        const formData = new FormData();
        formData.append('file', file);
        if (siteId) formData.append('siteId', siteId);
        if (farmLocationId) formData.append('farmLocationId', farmLocationId);
        if (transferDate) formData.append('transferDate', transferDate);
        formData.append('dairyComp', String(dairyComp ?? false));
        return uploadFreshStraw<FreshStrawTransferImportResponse>(
          '/api/fresh-straw/imports/transfers',
          formData,
        );
      },
      invalidatesTags: ['Lookups', 'FertilizationListFreshShipment'],
    }),
    getFreshStrawSiteByCode: build.query<FreshStrawSiteLookupResponse, string>({
      queryFn: async (code) => {
        const query = new URLSearchParams({ code }).toString();
        return fetchFreshStraw<FreshStrawSiteLookupResponse>(
          `/api/fresh-straw/lookups/site-by-code?${query}`,
        );
      },
      providesTags: ['Lookups'],
    }),
    getFreshStrawAccountByNumber: build.query<
      FreshStrawAccountLookupResponse,
      string
    >({
      queryFn: async (number) => {
        const query = new URLSearchParams({ number }).toString();
        return fetchFreshStraw<FreshStrawAccountLookupResponse>(
          `/api/fresh-straw/lookups/account-by-number?${query}`,
        );
      },
      providesTags: ['Lookups'],
    }),
    importFreshStrawPregCheck: build.mutation<
      FreshStrawPregCheckImportResponse,
      FreshStrawPregCheckImportArgs
    >({
      queryFn: async ({ file, defaultSiteName, faria }) => {
        const formData = new FormData();
        formData.append('file', file);
        if (defaultSiteName)
          formData.append('defaultSiteName', defaultSiteName);
        formData.append('faria', String(faria ?? false));
        return uploadFreshStraw<FreshStrawPregCheckImportResponse>(
          '/api/fresh-straw/imports/preg-check',
          formData,
        );
      },
      invalidatesTags: ['FertilizationListFreshShipment'],
    }),
    importFreshStrawFreshEmbryos: build.mutation<
      FreshStrawFreshEmbryoImportResponse,
      FreshStrawFreshEmbryoImportArgs
    >({
      queryFn: async ({ file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return uploadFreshStraw<FreshStrawFreshEmbryoImportResponse>(
          '/api/fresh-straw/imports/fresh-embryos',
          formData,
        );
      },
      invalidatesTags: ['FertilizationListFreshShipment'],
    }),
    importFreshStrawFreshEmbryosBatch: build.mutation<
      FreshStrawFreshEmbryoImportResponse,
      FreshStrawFreshEmbryoImportArgs
    >({
      queryFn: async ({ file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return uploadFreshStraw<FreshStrawFreshEmbryoImportResponse>(
          '/api/fresh-straw/imports/fresh-embryos/batch',
          formData,
        );
      },
      invalidatesTags: ['FertilizationListFreshShipment'],
    }),
    patchFreshStrawFertilization: build.mutation<
      { success: boolean },
      { fertilizationId: string } & FreshStrawFertPatch
    >({
      queryFn: async ({ fertilizationId, ...body }) =>
        fetchFreshStraw<{ success: boolean }>(
          `/api/fresh-straw/fertilizations/${fertilizationId}`,
          'PATCH',
          body,
        ),
      invalidatesTags: ['FertilizationListFreshShipment'],
    }),
  }),
});

export const {
  useGetFreshStrawAccountsRestQuery,
  useGetFreshStrawPredictionTechsRestQuery,
  useGetOfTransferOutsideEmbryosRestQuery,
  useUpdateOutsideEmbryosRestMutation,
  usePostFreshImplantMutation,
  usePostFreshFreezeMutation,
  usePostFreshDiscardMutation,
  useGetFreshStrawFertilizationsQuery,
  useLazyGetFreshStrawFertilizationsQuery,
  useGetFreshStrawFrozenEmbryosQuery,
  useGetFreshStrawFrozenEmbryosByShipmentQuery,
  useGetFreshStrawFlushesQuery,
  useGetFreshStrawFlushDetailQuery,
  useEnsureFreshStrawFlushEmbryosMutation,
  useMarkFreshStrawNoStimNoFlushMutation,
  useCreateFreshStrawOutsideEmbryoMutation,
  useLazySearchFreshStrawOnFarmEmbryosQuery,
  useLazySearchFreshStrawFertEmbryosQuery,
  useGetFreshStrawTransferredEmbryosQuery,
  useGetFreshStrawImplantDetailsQuery,
  useGetFreshStrawOutsideEmbryosByStopQuery,
  useGetFreshStrawTripMetaQuery,
  useUpdateFreshStrawTripMetaMutation,
  usePatchFreshStrawEmbryoMutation,
  useCreateFreshStrawDonorMutation,
  useCreateFreshStrawSireMutation,
  useDeactivateFreshStrawEmbryoMutation,
  useImplantFrozenFreshStrawEmbryoMutation,
  useStageFreshStrawEmbryosMutation,
  useMarkFreshStrawNoEmbryosProducedMutation,
  useReassignFreshStrawStrawsMutation,
  useRemoveFreshStrawBsFlagMutation,
  useGetFreshStrawEmbryoPartnershipsQuery,
  useCreateFreshStrawEmbryoPartnershipMutation,
  useDeleteFreshStrawEmbryoPartnershipMutation,
  useEndAllFreshStrawEmbryoPartnershipsMutation,
  useUpdateFreshStrawDegeneratesMutation,
  useVerifyFreshStrawBillableMutation,
  useImportFreshStrawTransfersMutation,
  useImportFreshStrawPregCheckMutation,
  useImportFreshStrawFreshEmbryosMutation,
  useImportFreshStrawFreshEmbryosBatchMutation,
  useGetFreshStrawSiteByCodeQuery,
  useGetFreshStrawAccountByNumberQuery,
  usePatchFreshStrawFertilizationMutation,
  useVerifyFreshShipmentMutation,
} = freshStrawRestApi;
